import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTheme } from '@mui/material'
import { useHistoryCustomBack, usePanelHistoryTop } from 'weblite-router'
import { useShowAudioPlayer } from '@weblite/audio'
import { Swiper, SwiperClass, SwiperRef, SwiperSlide } from 'swiper/react'
import { RoomTypes } from '@weblite/ts-action/room'
import {
  APP_BAR_HEIGHT_CSS_VAR,
  CHAT_LIST_APP_BAR_HEIGHT,
} from '$common/app-bar/app-bar.constants'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { FullPageListSkeleton } from '$common/full-page-list-skeleton'
import { useMultiSelectList } from '$common/multi-select-searchable-list-page'
import {
  RoomListItem,
  useRoomListItems,
  WORKSPACES,
} from '$logic/room-list/room-list.react-hook'
import { NAVIGATION_BAR_HEIGHT } from '$common/navigation-bar'
import {
  exitRoomListFromSelectionMode,
  setRoomListToSelectionMode,
} from '$logic/router'
import { AudioPlayer } from '$common/audio-player'
import { AUDIO_PLAYER_HEIGHT } from '$common/audio-player/audio-player-constants'
import { CallBar } from '$comp/_app/components/chat/components/call-bar'
import { TopBarsContainer } from '$common/top-bars-container'
import { useShowCallBar } from '$logic/call'
import { vibrate } from '$func/vibrate'
import { UnseenChip } from '$common/chip'
import {
  useRoomListUnseenCount,
  useWorkspacesUnseenCount,
} from '$logic/room-seen/react-hook'
import { toLocale } from '$func/text'
import { t } from '$i18n'
import { ITab, Tabs } from '$common/tabs/tabs'
import { ToolboxFab } from '$comp/_app/components/leftPanel/components/chat-list/components/toolbox-fab'
import { useIsDesktopSize } from '$common/react-hooks/layout'
import {
  INCREASE_LIMIT_OFFSET,
  useChatListScrollDirectionProducer,
  useItemsRenderLimit,
  useVisitedFolders,
} from './react-hooks'
import { EmptyChatList } from './components/empty-chat-list'
import { CreateRoomFab } from './components/create-room-fab'
import { ChatAppBar, ChatAppBarSelection } from './components/chat-app-bar'
import {
  STORY_RIBBON_HEIGHT_CSS_VAR,
  STORY_RIBBON_TRANSITION_MS,
} from '../../../story/constants'
import { StoryRibbonsApi } from '$comp/_app/components/story/story-ribbons-api'
import { StoryRibbons } from '$comp/_app/components/story/story-ribbons'
import {
  IS_STORY_ENABLED,
  SHOULD_CATEGORIZE_CHATS,
} from '$func/environmentInfo'
import { useStateRef } from '$hooks/use-state-ref'
import { StaticWappsFab } from '$comp/_app/components/leftPanel/components/chat-list/components/static-wapps-fab'
import {
  useWorkspacesIds,
  DEFAULT_WORKSPACE_ID,
  openWorkspace,
  useWorkspaces,
} from '$logic/workspace'
import { useCurrentWorkSpaceId } from '$comp/main/react-hook'
import { WORKSPACE_ENABLED } from '$config/env'
import { getAppbarHeight } from '$comp/_app/components/leftPanel/components/chat-list/helper'
import { AppbarApi } from '$comp/_app/components/leftPanel/components/chat-list/components/chat-app-bar/chat-app-bar-api'
import { dispatchSetWorkspace } from '$comp/main/root.action'
import { Scroller } from '$common/scroller'
import { CALL_BAR_HEIGHT } from '$comp/_app/components/chat/components/conf'
import useTopOverscroll from '$hooks/scroll/use-top-overscroll'
import { ListRenderer } from './list-renderer'
import {
  sendStartupProfilerEvent,
  StartupProfilerEventType,
} from '$logic/profiler'
import { WorkspaceAppBar } from './components/chat-app-bar/workspace-appbar'

export const TABS_HEIGHT = SHOULD_CATEGORIZE_CHATS ? 39 : 0
export const CHAT_LIST_WRAPPER_ID = 'chatListWrapper'

function a11yProps(index: number) {
  return {
    key: `chat-list-tab-${index}`,
    'aria-controls': `chat-list-tab-panel-${index}`,
  }
}

export let switchChatListTabExport:
  | ((newTabId: ChatSelectionTabTypes) => void)
  | null

export type ChatSelectionTabTypes = 'ALL' | 'WORKSPACES' | RoomTypes

export const defaultFolderChatsTypes = [
  'ALL',
  'PV',
  'CHANNEL',
  'GROUP',
] as ChatSelectionTabTypes[]

export function ChatList() {
  const {
    chatRoomListItems,
    workspaceRoomListItems,
    loading,
    workspacesChatIds,
  } = useRoomListItems()

  const rawWorkspaceIds = useWorkspacesIds()
  const workspaces = useWorkspaces()

  const swiperItems = useMemo(() => {
    return [
      ...defaultFolderChatsTypes.map(value => ({
        workspaceIdOrChatType: value,
        isWorkspace: value === 'WORKSPACES',
        workspaceName: '',
      })),
      ...(rawWorkspaceIds?.length
        ? [
            {
              workspaceIdOrChatType: 'WORKSPACES',
              isWorkspace: true,
              workspaceName: '',
            },
          ]
        : []),
      ...rawWorkspaceIds.map(value => ({
        workspaceIdOrChatType: value,
        isWorkspace: true,
        workspaceName: workspaces[value].name,
      })),
    ]
  }, [rawWorkspaceIds])

  const theme = useTheme()

  const containerRef = useRef<HTMLDivElement>(null)
  useChatListScrollDirectionProducer(containerRef)

  const currentWorkSpaceId = useCurrentWorkSpaceId()

  const isCurrentWorkSpaceDefault =
    currentWorkSpaceId === DEFAULT_WORKSPACE_ID || !WORKSPACE_ENABLED

  const chatListStoryRibbonHeight = isCurrentWorkSpaceDefault
    ? STORY_RIBBON_HEIGHT_CSS_VAR
    : '0px'

  const [triggerIsDisabled, setTriggerIsDisabled] = useState(false)

  const isDefaultWorkspace = currentWorkSpaceId === DEFAULT_WORKSPACE_ID

  const [currentChatsType, setCurrentChatsType] =
    useState<ChatSelectionTabTypes>('ALL')

  const selectionMapItems = useMemo(() => {
    const currentTabIndex = swiperItems.findIndex(
      item => item.workspaceIdOrChatType === currentChatsType,
    )

    return swiperItems[currentTabIndex].isWorkspace
      ? currentTabIndex === defaultFolderChatsTypes.length
        ? workspaceRoomListItems || []
        : workspacesChatIds[currentChatsType] || []
      : chatRoomListItems
  }, [currentChatsType])

  const {
    selectionMap,
    setSelection,
    resetSelection,
    selectedItems,
    hasSelection,
  } = useMultiSelectList<RoomListItem>({
    items: selectionMapItems,
    selectionPrimaryKey: 'chatId',
  })

  const handleShowStoryRibbon = useCallback(() => {
    StoryRibbonsApi.toggleStoryRibbons({ isShown: true })
  }, [])

  const handleHideStoryRibbon = useCallback(() => {
    StoryRibbonsApi.toggleStoryRibbons({ isShown: false })
  }, [])

  const overflowHappenedCallback = () => {
    if (IS_STORY_ENABLED) handleShowStoryRibbon()
    if (WORKSPACE_ENABLED) {
      const newHeight = getAppbarHeight(true, hasSelection, currentWorkSpaceId)
      AppbarApi.setAppbarHeight({ height: newHeight, expanded: true })
    }
  }

  const overflowResetCallback = useCallback(() => {
    handleHideStoryRibbon()
    const newHeight = getAppbarHeight(false, hasSelection, currentWorkSpaceId)
    AppbarApi.setAppbarHeight({ height: newHeight, expanded: false })
  }, [handleHideStoryRibbon, currentWorkSpaceId, hasSelection])

  const renderedOverflowTrigger = useTopOverscroll(
    containerRef,
    overflowHappenedCallback,
    overflowResetCallback,
    (!IS_STORY_ENABLED && !WORKSPACE_ENABLED) || triggerIsDisabled,
  )

  const sliderRef = useRef<SwiperRef>(null)
  const { showAudioPlayer } = useShowAudioPlayer()

  const panelMode = usePanelHistoryTop('roomList')?.state?.mode

  const panelModeRef = useStateRef(panelMode)

  const homePanelState = usePanelHistoryTop('home')?.state
  const isTop = homePanelState?.tab === 'roomList'

  useHistoryCustomBack(
    () => {
      if (hasSelection) exitRoomListFromSelectionMode()
      else return 'closeApp'
    },
    [hasSelection],
    isTop,
  )

  useEffect(() => {
    sendStartupProfilerEvent(StartupProfilerEventType.MOUNT_CHAT_LIST)
  }, [])

  useEffect(() => {
    if (hasSelection) StoryRibbonsApi.toggleStoryRibbons({ isShown: false })
  }, [hasSelection])

  // NOTE: Change panel mode when user select or deselect items
  useEffect(() => {
    if (panelModeRef.current === 'SELECT' && !selectedItems.length)
      exitRoomListFromSelectionMode()
    if (panelModeRef.current !== 'SELECT' && selectedItems.length)
      setRoomListToSelectionMode()
  }, [selectedItems])

  // NOTE: Deselect items when user clicked back button
  useEffect(() => {
    if (panelMode !== 'SELECT') {
      resetSelection()
    }
  }, [panelMode, resetSelection])

  const onItemSelectedChange = useCallback(
    (selected: boolean, chatId: string) => {
      setSelection(chatId, selected)
      if (selected) vibrate()
    },
    [setSelection],
  )

  const selectionCount = useMemo(
    () => Object.values(selectionMap).filter(Boolean).length,
    [selectionMap],
  )

  const handleChatTypeChange = useCallback(
    (chatTypeOrWorkspaceId: ChatSelectionTabTypes) => {
      const chatListWrapper = document.getElementById(CHAT_LIST_WRAPPER_ID)
      if (chatListWrapper && chatListWrapper.scrollTop > 1)
        chatListWrapper.scrollTo({
          // to prevent top over scroll
          top: 1,
          left: 0,
          behavior: 'smooth',
        })

      const newIndex = swiperItems.findIndex(
        type => type.workspaceIdOrChatType === chatTypeOrWorkspaceId,
      )
      sliderRef.current?.swiper.slideTo(newIndex)
      setCurrentChatsType(chatTypeOrWorkspaceId)
      if (newIndex > defaultFolderChatsTypes.length) {
        openWorkspace(swiperItems[newIndex].workspaceIdOrChatType)
      } else {
        openWorkspace(DEFAULT_WORKSPACE_ID)
      }
    },
    [currentChatsType],
  )

  useEffect(() => {
    switchChatListTabExport = handleChatTypeChange
  }, [handleChatTypeChange])

  useEffect(() => {
    setTriggerIsDisabled(true)
    AppbarApi.setAppbarHeight({
      height: getAppbarHeight(true, hasSelection, currentWorkSpaceId),
      expanded: true,
    })

    setTimeout(() => {
      setTriggerIsDisabled(false)
    }, 300)
  }, [currentWorkSpaceId])

  const unseenCountByRoomCategory = useRoomListUnseenCount()
  const unseenCountByWorkspaceId = useWorkspacesUnseenCount()

  const tabs = useMemo<ITab<ChatSelectionTabTypes>[]>(() => {
    return swiperItems.map((item, index) => {
      const { workspaceIdOrChatType, workspaceName } = item
      const unseenCount =
        index > defaultFolderChatsTypes.length
          ? unseenCountByWorkspaceId[workspaceIdOrChatType]
          : unseenCountByRoomCategory[
              workspaceIdOrChatType as ChatSelectionTabTypes
            ]

      return {
        label:
          index > defaultFolderChatsTypes.length
            ? workspaceName
            : t(`chatList.tabs.${workspaceIdOrChatType.toLowerCase()}`),
        icon: unseenCount ? (
          <UnseenChip
            color={
              workspaceIdOrChatType === currentChatsType
                ? 'neutral800'
                : 'neutral000'
            }
            label={
              unseenCount > 99 ? `+${toLocale(99)}` : toLocale(unseenCount)
            }
            sx={{
              borderColor: 'neutral300',
              borderWidth:
                workspaceIdOrChatType === currentChatsType ? '0' : '1px',
              borderStyle: 'solid',
            }}
          />
        ) : undefined,
        iconPosition: 'end',
        value: workspaceIdOrChatType,
        ...a11yProps(index),
      }
    })
  }, [unseenCountByRoomCategory, unseenCountByWorkspaceId, currentChatsType])

  const showCallBar = useShowCallBar()

  const { visitedFoldersMap, visitFolder } = useVisitedFolders()

  useEffect(() => {
    // NOTE: need to disable because appbar disappears and appears
    if (hasSelection) setChatListTransitionCSSVar('none')
    else
      setTimeout(() => {
        setChatListTransitionCSSVar(`height ${STORY_RIBBON_TRANSITION_MS}ms`)
      }, STORY_RIBBON_TRANSITION_MS + 50)
  }, [hasSelection])

  const audioPlayerHeight = showAudioPlayer ? AUDIO_PLAYER_HEIGHT : 0
  const callBarHeight = showCallBar ? CALL_BAR_HEIGHT : 0
  const tabsHeight = SHOULD_CATEGORIZE_CHATS ? TABS_HEIGHT : 0

  // const [showItems, setShowItems] = useState(true)
  // useEffect(() => {
  //   console.timeEnd('toggle')
  // }, [showItems])
  //
  // useEffect(() => {
  //   const handler = (e: any) => {
  //     if (e.code === 'KeyS') {
  //       console.time('toggle')
  //       setShowItems(show => !show)
  //       setCurrentChatsType('ALL')
  //     }
  //   }
  //
  //   window.addEventListener('keydown', handler)
  //   return () => {
  //     window.removeEventListener('keydown', handler)
  //   }
  // }, [])

  const swiperRef = useRef<SwiperClass>(null)

  const updateSwiperHeight = useCallback(() => {
    swiperRef.current?.updateAutoHeight()
  }, [])

  const onSwipeHandler = useCallback(
    (swiperIndex: number) => {
      const selectedSwipterItem = swiperItems[swiperIndex]

      setCurrentChatsType(selectedSwipterItem.workspaceIdOrChatType)
      visitFolder(selectedSwipterItem.workspaceIdOrChatType)
      if (swiperIndex > defaultFolderChatsTypes.length) {
        openWorkspace(swiperItems[swiperIndex].workspaceIdOrChatType)
      } else {
        openWorkspace(DEFAULT_WORKSPACE_ID)
      }
    },
    [swiperItems],
  )

  return (
    <PageWithAppBar
      bodySx={{
        position: 'relative',
        height: `calc(100% - ${chatListStoryRibbonHeight} - ${hasSelection ? `${CHAT_LIST_APP_BAR_HEIGHT}px` : APP_BAR_HEIGHT_CSS_VAR})`,
        flexGrow: 0,
        overflow: 'hidden',
        isolation: 'isolate',
        transition: 'var(--chat-list-transition)',
      }}
      appBar={
        <>
          {hasSelection ? (
            <ChatAppBarSelection selectedItems={selectedItems} />
          ) : isCurrentWorkSpaceDefault ? (
            <ChatAppBar />
          ) : (
            <WorkspaceAppBar />
          )}
          {isCurrentWorkSpaceDefault && <StoryRibbons />}
        </>
      }
    >
      {loading ? (
        <FullPageListSkeleton size="large" />
      ) : !(chatRoomListItems.length || workspaceRoomListItems.length) ? (
        <EmptyChatList />
      ) : (
        <>
          {SHOULD_CATEGORIZE_CHATS && (
            <Tabs
              tabs={tabs}
              tabsSx={{ height: TABS_HEIGHT, direction: 'ltr', flexShrink: 0 }}
              tabSx={{
                color: theme.palette.neutral900,
                '&.MuiTab-root': {
                  opacity: 0.7,
                },
                '&[data-selected="true"]': {
                  opacity: 1,
                },
              }}
              value={currentChatsType}
              onChange={handleChatTypeChange}
              isLtr
            />
          )}

          <TopBarsContainer
            isStatic
            hasAnyChild={showAudioPlayer || showCallBar}
          >
            {showCallBar && <CallBar />}
            {showAudioPlayer && <AudioPlayer />}
          </TopBarsContainer>
          <Scroller
            ref={containerRef}
            scrollableNodeChildrenSlot={renderedOverflowTrigger}
            // element="div"
            scrollableNodeProps={{
              id: CHAT_LIST_WRAPPER_ID,
            }}
            style={{
              height: `calc(100% - ${
                callBarHeight + tabsHeight + audioPlayerHeight
              }px)`,
              backgroundColor: theme.palette.neutral000,
            }}
          >
            {SHOULD_CATEGORIZE_CHATS ? (
              <Swiper
                ref={sliderRef}
                onInit={swiper => (swiperRef.current = swiper)}
                autoHeight
                dir="ltr"
                touchStartPreventDefault={false}
                onSlideChange={e => onSwipeHandler(e.realIndex)}
              >
                {swiperItems.map(
                  ({ workspaceIdOrChatType, isWorkspace }, index) => (
                    <SwiperSlide
                      key={workspaceIdOrChatType}
                      dir="rtl"
                      style={{
                        minHeight: `calc(100% - ${
                          callBarHeight + tabsHeight + audioPlayerHeight
                        }px - ${STORY_RIBBON_HEIGHT_CSS_VAR} - ${APP_BAR_HEIGHT_CSS_VAR} )`,
                      }}
                    >
                      <ListRenderer
                        key={workspaceIdOrChatType}
                        selectionCount={selectionCount}
                        selectionMap={selectionMap}
                        items={
                          isWorkspace
                            ? index === defaultFolderChatsTypes.length
                              ? workspaceRoomListItems || []
                              : workspacesChatIds[workspaceIdOrChatType]
                            : chatRoomListItems
                        }
                        onItemSelectedChange={onItemSelectedChange}
                        chatType={
                          isWorkspace
                            ? undefined
                            : (workspaceIdOrChatType as ChatSelectionTabTypes)
                        }
                        visited={
                          index > defaultFolderChatsTypes.length
                            ? visitedFoldersMap.ALL
                            : visitedFoldersMap[
                                workspaceIdOrChatType as ChatSelectionTabTypes
                              ]
                        }
                        onUpdateHeight={updateSwiperHeight}
                      />
                    </SwiperSlide>
                  ),
                )}
              </Swiper>
            ) : (
              <ListRenderer
                selectionCount={selectionCount}
                selectionMap={selectionMap}
                items={chatRoomListItems}
                onItemSelectedChange={onItemSelectedChange}
                chatType={'ALL'}
                visited={visitedFoldersMap.ALL}
              />
            )}
          </Scroller>
          <ToolboxFab />
          <StaticWappsFab />
          <CreateRoomFab />
        </>
      )}
    </PageWithAppBar>
  )
}

export function setChatListTransitionCSSVar(transition: string) {
  document.documentElement?.style.setProperty(
    '--chat-list-transition',
    transition,
  )
}

export const ChatListApi = {
  switchChatListTab(id: string) {
    switchChatListTabExport?.(id)
  },
}
